/**
 * PostHog API Deduplication Utility
 * 
 * Prevents duplicate PostHog operations (capture, identify, setPersonProperties, etc.)
 * This ensures that ALL PostHog automatic tracking events are deduplicated,
 * not just manually tracked events.
 * 
 * PostHog automatically tracks certain operations as events:
 * - "Set person properties" when setPersonProperties() is called
 * - "$identify" when identify() is called
 * - Custom events when capture() is called
 * 
 * This utility deduplicates at the API level to prevent all duplicate events.
 */

interface TrackedOperation {
  timestamp: number;
  methodName: string;
  hash: string;
}

// Single shared deduplication store for all PostHog operations
const trackedOperations: Map<string, TrackedOperation> = new Map();
const DEDUPE_WINDOW_MS = 10000; // 10 seconds - prevent duplicates within this window

/**
 * Create a stable hash from method name and parameters
 * Normalizes parameters to ensure consistent hashing
 */
function createOperationHash(
  methodName: string,
  ...args: any[]
): string {
  // Normalize arguments for consistent hashing
  const normalizedArgs = args.map(arg => {
    if (arg === null || arg === undefined) {
      return null;
    }
    
    // For objects, sort keys and remove undefined/null/empty values
    if (typeof arg === 'object' && !Array.isArray(arg) && arg.constructor === Object) {
      const normalized: Record<string, any> = {};
      const sortedKeys = Object.keys(arg).sort();
      for (const key of sortedKeys) {
        const value = arg[key];
        if (value !== undefined && value !== null && value !== '') {
          normalized[key] = value;
        }
      }
      return normalized;
    }
    
    // For arrays, keep as-is but normalize objects within
    if (Array.isArray(arg)) {
      return arg.map(item => 
        typeof item === 'object' && item !== null && item.constructor === Object
          ? createOperationHash('', item) // Recursively normalize objects in arrays
          : item
      );
    }
    
    return arg;
  });
  
  // Create stable hash string
  const hashString = JSON.stringify({
    method: methodName,
    args: normalizedArgs,
  });
  
  return hashString;
}

/**
 * Check if a PostHog operation was recently performed (within dedupe window)
 * Returns true if duplicate, false if new operation
 * 
 * @param methodName - Name of the PostHog method (e.g., 'capture', 'identify', 'setPersonProperties')
 * @param args - Arguments passed to the method
 * @returns true if duplicate, false if new
 */
export function isDuplicatePostHogOperation(
  methodName: string,
  ...args: any[]
): boolean {
  const hash = createOperationHash(methodName, ...args);
  const now = Date.now();
  const tracked = trackedOperations.get(hash);
  
  if (tracked) {
    const timeSinceTracked = now - tracked.timestamp;
    if (timeSinceTracked < DEDUPE_WINDOW_MS) {
      return true;
    }
    // Remove old entry if outside window
    trackedOperations.delete(hash);
  }
  
  // Record this operation
  trackedOperations.set(hash, {
    timestamp: now,
    methodName,
    hash,
  });
  
  // Clean up old entries (older than dedupe window)
  const cutoff = now - DEDUPE_WINDOW_MS;
  const keysToDelete: string[] = [];
  trackedOperations.forEach((operation, key) => {
    if (operation.timestamp < cutoff) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => trackedOperations.delete(key));
  
  return false;
}

/**
 * Clear all tracked operations (useful for testing or reset)
 */
export function clearTrackedPostHogOperations(): void {
  trackedOperations.clear();
}

/**
 * Wrapper function to deduplicate PostHog method calls
 * Use this to wrap any PostHog method call to prevent duplicates
 * 
 * @param posthog - PostHog instance
 * @param methodName - Name of the method to call
 * @param args - Arguments to pass to the method
 * @returns Result of the method call, or undefined if duplicate
 */
export function deduplicatedPostHogCall(
  posthog: any,
  methodName: string,
  ...args: any[]
): any {
  if (!posthog) {
    console.warn(`⚠️ [PostHog Dedupe] PostHog instance is null, cannot call ${methodName}`);
    return undefined;
  }
  
  // Check for duplicates
  if (isDuplicatePostHogOperation(methodName, ...args)) {
    return undefined; // Duplicate operation, skip
  }
  
  // Execute the method
  if (typeof posthog[methodName] === 'function') {
    try {
      const result = posthog[methodName](...args);
      return result;
    } catch (error) {
      console.error(`❌ [PostHog Dedupe] Error calling ${methodName}:`, error);
      // Remove from tracked operations on error so it can be retried
      const hash = createOperationHash(methodName, ...args);
      trackedOperations.delete(hash);
      throw error;
    }
  } else {
    console.warn(`⚠️ [PostHog Dedupe] Method ${methodName} does not exist on PostHog instance`);
    return undefined;
  }
}

